import { useEffect, useMemo, useRef, useState } from "react";
import {
  formatTimezoneLabel,
  getTimezones,
  timezoneOffsetLabel,
} from "@/lib/timezone";

interface TimezoneSelectProps {
  value: string;
  onChange: (zone: string) => void;
  className?: string;
}

/** Australian / New Zealand zones pinned to the top for quick access. */
const AUS_NZ_ZONES = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Adelaide",
  "Australia/Perth",
  "Australia/Darwin",
  "Australia/Hobart",
  "Australia/Canberra",
  "Australia/Lord_Howe",
  "Australia/Eucla",
  "Pacific/Auckland",
  "Pacific/Chatham",
  "Pacific/Norfolk",
];

export function TimezoneSelect({ value, onChange, className }: TimezoneSelectProps) {
  const allZones = useMemo(() => getTimezones(), []);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const pinnedZones = useMemo(
    () => AUS_NZ_ZONES.filter((z) => allZones.includes(z)),
    [allZones],
  );
  const otherZones = useMemo(
    () => allZones.filter((z) => !AUS_NZ_ZONES.includes(z)),
    [allZones],
  );

  const q = query.trim().toLowerCase();
  const isSearching = q !== "";

  const filtered = useMemo(() => {
    if (!isSearching) return allZones;
    return allZones.filter((z) => {
      const label = formatTimezoneLabel(z).toLowerCase();
      return z.toLowerCase().includes(q) || label.includes(q);
    });
  }, [allZones, isSearching, q]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const renderOption = (zone: string) => (
    <button
      key={zone}
      type="button"
      role="option"
      aria-selected={zone === value}
      onClick={() => {
        onChange(zone);
        setQuery("");
        setOpen(false);
      }}
      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left whitespace-nowrap hover:bg-slate-50 ${
        zone === value ? "text-emerald-700 font-medium" : "text-slate-700"
      }`}
    >
      <span>{formatTimezoneLabel(zone)}</span>
      <span className="text-xs text-slate-400">{timezoneOffsetLabel(zone)}</span>
    </button>
  );

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        value={open ? query : formatTimezoneLabel(value)}
        placeholder="Search timezone…"
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={(e) => {
          setQuery("");
          setOpen(true);
          e.target.select();
        }}
        className="w-full px-3 py-2 pr-8 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
      />
      <i
        className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none"
        aria-hidden="true"
      />
      {open && (
        <div
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-slate-200 rounded-lg shadow-sm"
        >
          {isSearching ? (
            filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No matching timezone</div>
            ) : (
              filtered.map(renderOption)
            )
          ) : (
            <>
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Australia &amp; New Zealand
              </div>
              {pinnedZones.map(renderOption)}
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                All timezones
              </div>
              {otherZones.map(renderOption)}
            </>
          )}
        </div>
      )}
    </div>
  );
}